import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { userServiceApiSlice } from "state/services/userServiceApiSlice";

// How long the page waits before reloading the quick cards when nobody has pressed Refresh.
export const AUTO_REFRESH_MS = 5 * 60 * 1000;
// The slow reports (the summary and the people list) reload on their own less often, so a busy server isn't asked for them
// as frequently. Pressing Refresh still reloads everything.
export const HEAVY_AUTO_REFRESH_MS = 15 * 60 * 1000;

// Refresh for the Metered Usage pages. Metered usage queries are tagged "MeteredUsage" (all of them) plus "MeteredUsageLight"
// (the quick ones) or "MeteredUsageHeavy" (the summary and the people list). Pressing Refresh reloads all of them. On its own
// the page reloads the quick ones every 5 minutes and the slow ones every 15, each counted from its own last reload, paused
// while the browser tab is hidden, and straight away on return after being away longer than that.
export const useMeteredUsageRefresh = () => {
  const dispatch = useDispatch();
  const lastLightRef = useRef(Date.now());
  const lastHeavyRef = useRef(Date.now());

  const refresh = useCallback(() => {
    lastLightRef.current = Date.now();
    lastHeavyRef.current = Date.now();
    dispatch(userServiceApiSlice.util.invalidateTags(["MeteredUsage"]));
  }, [dispatch]);

  useEffect(() => {
    const refreshIfStale = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      // Slow ones first when both are due, so they are reloaded together (one pass), not as two separate bursts.
      const heavyDue = now - lastHeavyRef.current >= HEAVY_AUTO_REFRESH_MS;
      const lightDue = now - lastLightRef.current >= AUTO_REFRESH_MS;
      const tags = [];
      if (lightDue) {
        tags.push("MeteredUsageLight");
        lastLightRef.current = now;
      }
      if (heavyDue) {
        tags.push("MeteredUsageHeavy");
        lastHeavyRef.current = now;
      }
      if (tags.length) dispatch(userServiceApiSlice.util.invalidateTags(tags));
    };
    const timer = setInterval(refreshIfStale, 30 * 1000);
    document.addEventListener("visibilitychange", refreshIfStale);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshIfStale);
    };
  }, [dispatch]);

  return refresh;
};
